import React, { Suspense } from 'react';

import type { EnvironmentType } from '@atlassian/forge-ui-types';

import { RelayErrorBoundary } from '../../error-boundary/RelayErrorBoundary';
import type { GQLCustomUITunnelDefinition } from '../../web-client/graphql/types';
import {
	useCustomUITunnelsListRelay,
	type UseAppTunnelsRelayOptions,
} from '../../web-client/hooks/useCustomUITunnelsList';

interface TunnelResult {
	loading: boolean;
	tunnels: GQLCustomUITunnelDefinition[];
}

interface TunnelsProviderProps extends UseAppTunnelsRelayOptions {
	children: (result: TunnelResult) => React.ReactElement;
	environmentType: EnvironmentType;
}

interface TunnelsLoaderProps extends UseAppTunnelsRelayOptions {
	children: (result: TunnelResult) => React.ReactElement;
}

const TunnelsLoader = ({ appId, contextIds, environmentId, children }: TunnelsLoaderProps) => {
	const { tunnels } = useCustomUITunnelsListRelay({
		appId,
		environmentId,
		contextIds,
	});

	return children({ loading: false, tunnels });
};

export const TunnelsProvider = ({
	environmentType,
	appId,
	contextIds,
	environmentId,
	children,
}: TunnelsProviderProps): React.JSX.Element => {
	const fallbackResult: TunnelResult = { tunnels: [], loading: true };

	if (environmentType === 'DEVELOPMENT') {
		return (
			<RelayErrorBoundary fallback={children({ tunnels: [], loading: false })}>
				<Suspense fallback={children(fallbackResult)}>
					<TunnelsLoader
						appId={appId}
						contextIds={contextIds}
						environmentId={environmentId}
						children={children}
					/>
				</Suspense>
			</RelayErrorBoundary>
		);
	}

	return children({ loading: false, tunnels: [] });
};
